import { ForwardedRef, forwardRef } from 'react';

import { Flex } from '@chakra-ui/react';
import { InitiativeTaskRow } from 'components/RabbitInitiativeSidePanel/PrimaryPanel/Sections/Task/InitiativeTaskRow';
import { onSubtaskChange } from 'components/RabbitInitiativeSidePanel/initiativeSecondaryPanelSlice';
import { useDispatch } from 'react-redux';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';

import { Plus } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/noInitiatives.svg';

import { Button, BUTTON_VARIANT } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { setSecondaryPanelOpen, setSecondaryScreen } from '../competencyDrawerSlice';
import { SectionContainer } from '../components/SectionContainer';
import { useGetCompetencyTasksQuery } from '../dataHooks';
import { useCompetencyQueryParams } from '../helpers/useCompetencyQueryParams';
import { SecondaryDrawerScreens } from '../types/slice';

const CreateInitiativeButton =
  ({ openAddInitiativePanel }: { openAddInitiativePanel: () => void }) =>
  () => (
    <Button StartIcon={Plus} variant={BUTTON_VARIANT.outlined} onClick={openAddInitiativePanel}>
      Add New Initiative
    </Button>
  );

export const Initiatives = forwardRef((_, ref: ForwardedRef<HTMLDivElement>) => {
  const { isLoading, data: initiatives } = useGetCompetencyTasksQuery();

  const { employeeId } = useCompetencyQueryParams();
  const { data: employee } = useProfilePage({ employeeId });

  const dispatch = useDispatch();

  const openAddInitiativePanel = () => {
    const updatedToIds = [
      {
        uuid: employee?.id,
        ownerType: 'OWNER',
        metadata: { displayName: employee?.displayName },
        profile: { profilePhoto: employee?.profilePhotoSrc },
      },
    ];
    dispatch(
      onSubtaskChange({
        toIds: updatedToIds,
      })
    );
    dispatch(setSecondaryPanelOpen(true));
    dispatch(setSecondaryScreen(SecondaryDrawerScreens.ADD_INITIATIVE));
  };

  return (
    <SectionContainer gap={2} ref={ref}>
      <Flex alignItems="center" justifyContent="space-between" mx={3}>
        <TextBodyMedium bold>Initiatives</TextBodyMedium>
        <Button StartIcon={Plus} variant={BUTTON_VARIANT.outlined} onClick={openAddInitiativePanel}>
          Add New Initiative
        </Button>
      </Flex>
      <Divider borderColor="#CFD7DF" />
      {isLoading ? (
        <SkeletonLoader />
      ) : (
        initiatives.map((initiative) => (
          <InitiativeTaskRow
            key={initiative.uuid}
            id={initiative.uuid}
            goalName={initiative.text}
            taskStatus={initiative.detailedStatus}
            dueDate={new Date(initiative.dueTime?.timeStamp)}
            employees={initiative.toIds}
            showDivider
            data={initiative}
            status={initiative.status}
            panelType="COMPETENCY"
            parentId={initiative.uuid}
            visibilityType={initiative.visibilityType}
            employeeId={employeeId}
          />
        ))
      )}
      {!isLoading && initiatives.length === 0 ? (
        <EmptyScreen
          heading="No initiatives to show. Create one!"
          description="The best way to have a garden full of roses is to plant them! Take initiative and get started today!"
          illustrationSrc={NoDataImg}
          CTAComp={CreateInitiativeButton({ openAddInitiativePanel })}
        />
      ) : null}
    </SectionContainer>
  );
});
